import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';
import { authState } from '@/recoil/auth';
import { absentModalState, pharmacyState } from '@/recoil/pharmacy';
import api from '@/apis/pharmacy';
import GlobalAlert from '@/components/GlobalAlert';
import { getErrorMessage } from '@/utils/error';
import { AxiosError } from 'axios';
import { PharmacyDuty, VroongShippingLabelParams } from '@/apis/pharmacy/interface';
import {
  getPharmacyContactInformation,
  getPharmacyDetail,
  getShippingLabel,
  postBePharmacyAbsent,
  postBePharmacyPresent,
  postDeliveryReceipt,
  resetOrderByPharmacist,
  updatePharmacyContacts,
  updatePriceByPharmacist
} from '@/utils/graphqlApis';
import * as bff_types from '@/api/bff_types';
import { ShippingCompany } from '@/api/bff_types';

const usePharmacyQuery = () => {
  const { token } = useRecoilValue(authState);
  const absentModal = useRecoilValue(absentModalState);
  const queryClient = useQueryClient();
  const pharmacy = useRecoilValue(pharmacyState);

  /** [약국 정보] 약국 정보 조회 */
  const pharmacyDetailQuery = () => {
    return useQuery(['pharmacyDetail', token], () => getPharmacyDetail(token), {
      enabled: !!token
    });
  };
  /** [약국 운영] 휴식 시간 on/off */
  const operatingMutation = useMutation(
    (type: 'OPEN' | 'CLOSE') =>
      api.setPharmacyOperating({
        token,
        pharmacyId: pharmacy.id,
        type: type
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pharmacyDetail']);
        queryClient.invalidateQueries(['dayOfWeek']);
      },
      onError: (err: AxiosError) => {
        GlobalAlert.show({ title: getErrorMessage(err) });
      }
    }
  );

  /** [약국 운영] 운영 시간 변경 */
  const dutyTimeMutation = useMutation(
    (dutyTimes: Array<PharmacyDuty>) =>
      api.setPharmacyDutyTime(token, dutyTimes),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pharmacyDetail']);
        queryClient.invalidateQueries(['pharmacyContacts']);
        queryClient.invalidateQueries(['dayOfWeek']);
      },
      onError: (err: AxiosError) => {
        GlobalAlert.show({ title: getErrorMessage(err) });
      }
    }
  );

  const requestVroongShippingLabelMutation = useMutation(
    (params: VroongShippingLabelParams) =>
      api.requestVroongShippingLabel(token, params.orderId, params.addShipping),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['order']);
        queryClient.invalidateQueries(['requestedOrders']);
        queryClient.invalidateQueries(['preparingOrders']);
      }
    }
  );

  const requestDoobalHeroShippingLabelMutation = useMutation(
    (orderId: number) => api.requestDoobalHeroShippingLabel(token, orderId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['order']);
        queryClient.invalidateQueries(['requestedOrders']);
        queryClient.invalidateQueries(['preparingOrders']);
      }
    }
  );

  const requestTeamFreshShippingLabelMutation = useMutation(
    (orderNumber: string) =>
      getShippingLabel(token, {
        orderNumber: orderNumber,
        shippingCompany: ShippingCompany.TEAM_FRESH
      })
  );

  const requestTeamFreshDeliveryReceiptMutation = useMutation(
    (orderNumber: string) =>
      postDeliveryReceipt(token, {
        orderNumber: orderNumber,
        shippingCompany: ShippingCompany.TEAM_FRESH
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['order']);
        queryClient.invalidateQueries(['requestedOrders']);
        queryClient.invalidateQueries(['preparingOrders']);
      }
    }
  );

  const requestBePharmacyPresentMutation = useMutation(
    () => postBePharmacyPresent(token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pharmacyDetail']);
        queryClient.invalidateQueries(['pharmacyContacts']);
      }
    }
  );

  const requestBePharmacyAbsentMutation = useMutation(
    () => postBePharmacyAbsent(token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pharmacyDetail']);
        queryClient.invalidateQueries(['pharmacyContacts']);
      }
    }
  );

  const pharmacyContactsInformationQuery = () =>
    useQuery(
      ['pharmacyContacts', token, absentModal],
      () => getPharmacyContactInformation(token),
      {
        enabled: !!token,
        refetchInterval: 1000 * 60 * 5
      }
    );

  const pharmacyContactsUpdateMutation = useMutation(
    (request: bff_types.PharmacyContactUpdateRequestInput[]) =>
      updatePharmacyContacts(token, request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pharmacyContacts']);
      }
    }
  );

  const orderPriceUpdateMutation = useMutation(
    (request: bff_types.OrderModifyPriceMutationRequestInput) =>
      updatePriceByPharmacist(token, request)
  );

  const orderResetUpdateMutation = useMutation(
    (request: bff_types.InitOrderByPharmacistMutationRequestInput) =>
      resetOrderByPharmacist(token, request)
  );

  return {
    pharmacyDetailQuery,
    operatingMutation,
    dutyTimeMutation,
    requestVroongShippingLabelMutation,
    requestDoobalHeroShippingLabelMutation,
    requestTeamFreshShippingLabelMutation,
    requestTeamFreshDeliveryReceiptMutation,
    requestBePharmacyPresentMutation,
    requestBePharmacyAbsentMutation,
    pharmacyContactsInformationQuery,
    pharmacyContactsUpdateMutation,
    orderPriceUpdateMutation,
    orderResetUpdateMutation
  };
};

export default usePharmacyQuery;
